
import ProjectSubmissionStore from "@/stores/ProjectSubmissionStore";
import AnswerBatchUploader from "../../../CompareNeoPenData/AnswerBatchUploader";
import GAIBatchStrokeRecognizer from "../../../CompareNeoPenData/GAIBatchStrokeRecognizer";
import ProjectStore from "@/stores/ProjectStore";
import { IExamInfo } from "@/repositories/model/transfer/IExamInfo";
import { IOcrResult } from "@/ndp/hooks/useStrokeRecognition";
import { UploadCallbacks, USBBatchStrokeUploader, USBBatchUploadItem } from "./USBBatchStrokeUploader";
import BatchStrokeRecognizer from "../../../CompareNeoPenData/BatchStrokeRecognizer";
import NeoPenStore from "@/stores/NeoPenStore";
import { StrokeRecognitionSwitcher } from "@/common/objects/StrokeRecognitionSwitcher";


export class USBBatchProcessor {

    private projectCode: string;
    private projectSubmissionStore: ProjectSubmissionStore;
    private projectStore: ProjectStore;
    private neoPenStore: NeoPenStore;

    private items: USBBatchUploadItem[];
    private examInfoArray: IExamInfo[];
    private callbacks: UploadCallbacks | undefined;
    private uploader: USBBatchStrokeUploader | null = null;
    private gaiRecognizer: GAIBatchStrokeRecognizer | null = null;
    private msRecognizer: BatchStrokeRecognizer | null = null;
	private gaiResult: Record<string, string> | null = null;
    private answerUploader: AnswerBatchUploader | null = null;
    // For future multi-uploader orchestration
    private segments: { offset: number; count: number }[] = [];
    private usbOffset: number | null = null;
    private gaiOffset: number | null = null;
    private answerOffset: number | null = null;
    
    constructor(
        projectCode: string,
        items: USBBatchUploadItem[],
        examInfoArray: IExamInfo[],
        projectSubmissionStore: ProjectSubmissionStore,
        projectStore: ProjectStore,
        neoPenStore: NeoPenStore,
        callbacks?: UploadCallbacks    
    ) {
        this.projectCode = projectCode;
        this.projectSubmissionStore = projectSubmissionStore;
        this.projectStore = projectStore;
        this.neoPenStore = neoPenStore;
        this.items = items;
        this.examInfoArray = examInfoArray;
        this.callbacks = callbacks;
        // Segments will be registered on-demand for each phase (USB, GAI)
        this.segments = [];
    }

    public async uploadStrokes() {

        // 1) USB upload (existing)
        if (!this.uploader) {
            if (this.usbOffset == null) this.usbOffset = this.registerSegment(this.items.length);
            const baseUsbCallbacks = this.createDelegatingCallbacks({ offset: 0, count: this.items.length });
            const usbCallbacks: UploadCallbacks = {
                ...baseUsbCallbacks,
                onItemError: (i, item, e) => {
                    baseUsbCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseUsbCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.uploader = new USBBatchStrokeUploader(this.projectCode, this.projectSubmissionStore, this.items, usbCallbacks);
        }

        const usbResult = await this.uploader.upload();
        try {
            const failed = usbResult?.failedIndices ?? [];
            const successCount = usbResult?.allSucceeded ? this.items.length : Math.max(0, Math.min(...failed));
            this.callbacks?.onUsbStageComplete?.(successCount, failed);
        } catch (_e) { /* ignore */ }
        if (!usbResult.allSucceeded) {
            this.notifyPipelineComplete('failure', 'usb', usbResult.failedIndices ?? []);
            return; // stop pipeline if USB had failures
        }
        // USB done, waiting for GAI stage
        this.notifyPipelineComplete('waiting', 'gai', []);

        // 2) GAIBatchStrokeRecognizer after USB finishes (only if USB succeeded fully)
        // Both msRecognizer and gaiRecognizer is initialized
        if (!this.gaiRecognizer) {
            const gaiItems = this.items.map(it => ({
                ncodeRange: it.range,
                wholeStrokes: it.strokes,
                userCode: it.userCode,
            }));
            if (this.gaiOffset == null) this.gaiOffset = this.registerSegment(gaiItems.length);
            const baseGaiCallbacks = this.createDelegatingCallbacks({ offset: this.usbOffset, count: gaiItems.length });
            const gaiCallbacks: UploadCallbacks = {
                ...baseGaiCallbacks,
                onItemError: (i, item, e) => {
                    baseGaiCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseGaiCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.gaiRecognizer = new GAIBatchStrokeRecognizer(this.examInfoArray, gaiItems, { key: `${this.projectCode}`, callbacks: gaiCallbacks });
        }
        if (!this.msRecognizer) {
            const gaiItems = this.items.map(it => ({
                ncodeRange: it.range,
                wholeStrokes: it.strokes,
                userCode: it.userCode,
            }));
            if (this.gaiOffset == null) this.gaiOffset = this.registerSegment(gaiItems.length);
            const baseGaiCallbacks = this.createDelegatingCallbacks({ offset: this.usbOffset, count: gaiItems.length });
            const gaiCallbacks: UploadCallbacks = {
                ...baseGaiCallbacks,
                onItemError: (i, item, e) => {
                    baseGaiCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseGaiCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.msRecognizer = new BatchStrokeRecognizer(this.neoPenStore, this.examInfoArray, gaiItems, { key: `${this.projectCode}`, callbacks: gaiCallbacks });
        }

        let gaiStageResult: {allSucceeded: boolean; result: Record<string, string>; failedIndices: number[];};
        if (StrokeRecognitionSwitcher.instance.isUsingOpenAI == true) {
            gaiStageResult = await this.gaiRecognizer.recognize();
        } else {
            gaiStageResult = await this.msRecognizer.recognize();
        }

        this.gaiResult = { ...(this.gaiResult ?? {}), ...(gaiStageResult?.result ?? {}) };
        if (!gaiStageResult?.allSucceeded) {
            this.notifyPipelineComplete('failure', 'gai', gaiStageResult?.failedIndices ?? []);
            return; // stop pipeline if GAI had failures
        }
        // GAI done, waiting for ANSWER stage
        this.notifyPipelineComplete('waiting', 'answer', []);

        // 3) AnswerBatchUploader after GAIBatch finished (only if GAIBatch succeeded fully)
        

        const categorized: Record<string, { [questionCode: string]: IOcrResult }> = {};
        const userCodes = Array.from(new Set(this.items.map(it => it.userCode)));
        for (const [code, recognized] of Object.entries(this.gaiResult ?? {})) {
            const matchedUser = userCodes.find(uc => code.startsWith(`${uc}-`));
            if (!matchedUser) continue;
            const restKey = code.slice(matchedUser.length + 1);
            if (!categorized[matchedUser]) categorized[matchedUser] = {};
            categorized[matchedUser][restKey] = { recognizedText: recognized, ocrStatus: "done" } as IOcrResult;
        }

        const answerItems = this.items.map(it => ({
            userCode: it.userCode,
            range: it.range,
            codeToRecognizedText: [categorized[it.userCode] ?? {}],
            submissionCode: it.submissionCode
        }));

        if (this.answerOffset == null) this.answerOffset = this.registerSegment(answerItems.length);
        const baseAnswerCallbacks = this.createDelegatingCallbacks({ offset: this.gaiOffset, count: answerItems.length });
        const answerCallbacks: UploadCallbacks = {
            ...baseAnswerCallbacks,
            onItemError: (i, item, e) => {
                baseAnswerCallbacks.onItemError?.(i, item, e);
            },
            onComplete: ({ failedIndices }) => {
                baseAnswerCallbacks.onComplete?.({ failedIndices });
            }
        };
        if (!this.answerUploader) {
            this.answerUploader = new AnswerBatchUploader(this.projectCode, this.examInfoArray, this.projectSubmissionStore, this.projectStore, answerItems);
            this.answerUploader.setCallbacks(answerCallbacks);            
        }
        const answerResult = await this.answerUploader.upload();
        if (!answerResult.allSucceeded) {
            this.notifyPipelineComplete('failure', 'answer', answerResult.failedIndices ?? []);
            return; // stop pipeline if Answer had failures
        }
        this.notifyPipelineComplete('success', 'answer', []);
    }


    public async resume() {
        // First resume USB
        if (!this.uploader) {
            if (this.usbOffset == null) this.usbOffset = this.registerSegment(this.items.length);
            const baseUsbCallbacks = this.createDelegatingCallbacks({ offset: 0, count: this.items.length });
            const usbCallbacks: UploadCallbacks = {
                ...baseUsbCallbacks,
                onItemError: (i, item, e) => {
                    baseUsbCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseUsbCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.uploader = new USBBatchStrokeUploader(this.projectCode, this.projectSubmissionStore, this.items, usbCallbacks);
        }

        // Reset flag before resuming; callbacks will set true only if failures remain
        const usbResult = await this.uploader.resume();
        try {
            const failed = usbResult?.failedIndices ?? [];
            const successCount = usbResult?.allSucceeded ? this.items.length : Math.max(0, Math.min(...failed));
            this.callbacks?.onUsbStageComplete?.(successCount, failed);
        } catch (_e) { /* ignore */ }

        // 2) GAIBatchStrokeRecognizer after USB finishes (only if USB succeeded fully)
        if (!usbResult.allSucceeded) {
            this.notifyPipelineComplete('failure', 'usb', usbResult.failedIndices ?? []);
            return; // stop pipeline if USB had failures
        }
        this.notifyPipelineComplete('waiting', 'gai', []);

        // instantiate both msRecognizer and gaiRecognizer
        if (!this.gaiRecognizer) {
            const gaiItems = this.items.map(it => ({ ncodeRange: it.range, wholeStrokes: it.strokes, userCode: it.userCode }));
            if (this.gaiOffset == null) this.gaiOffset = this.registerSegment(gaiItems.length);
            const baseGaiCallbacks = this.createDelegatingCallbacks({ offset: this.usbOffset, count: gaiItems.length });
            const gaiCallbacks: UploadCallbacks = {
                ...baseGaiCallbacks,
                onItemError: (i, item, e) => {
                    baseGaiCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseGaiCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.gaiRecognizer = new GAIBatchStrokeRecognizer(this.examInfoArray, gaiItems, { key: `${this.projectCode}`, callbacks: gaiCallbacks });
        }
        if (!this.msRecognizer) {
            const gaiItems = this.items.map(it => ({ ncodeRange: it.range, wholeStrokes: it.strokes, userCode: it.userCode }));
            if (this.gaiOffset == null) this.gaiOffset = this.registerSegment(gaiItems.length);
            const baseGaiCallbacks = this.createDelegatingCallbacks({ offset: this.usbOffset, count: gaiItems.length });
            const gaiCallbacks: UploadCallbacks = {
                ...baseGaiCallbacks,
                onItemError: (i, item, e) => {
                    baseGaiCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseGaiCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.msRecognizer = new BatchStrokeRecognizer(this.neoPenStore, this.examInfoArray, gaiItems, { key: `${this.projectCode}`, callbacks: gaiCallbacks });
        }

        // Reset flag before resuming GAI; callbacks will set true only if failures remain
        let gaiResumeResult: {allSucceeded: boolean; result: Record<string, string>; failedIndices: number[];};
        if (StrokeRecognitionSwitcher.instance.isUsingOpenAI == true) {
            gaiResumeResult = await this.gaiRecognizer.resume();
        } else {
            gaiResumeResult = await this.msRecognizer.resume();
        }

        this.gaiResult = { ...(this.gaiResult ?? {}), ...(gaiResumeResult?.result ?? {}) };
        if (!gaiResumeResult?.allSucceeded) {
            this.notifyPipelineComplete('failure', 'gai', gaiResumeResult?.failedIndices ?? []);
            return; // stop pipeline if GAI had failures
        }
        this.notifyPipelineComplete('waiting', 'answer', []);

        // Finally resume AnswerBatchUploader only if GAI succeeded
        
        if (!this.answerUploader) {
            const categorized: Record<string, { [questionCode: string]: IOcrResult }> = {};
            const userCodes = Array.from(new Set(this.items.map(it => it.userCode)));
            for (const [code, recognized] of Object.entries(this.gaiResult ?? {})) {
                const matchedUser = userCodes.find(uc => code.startsWith(`${uc}-`));
                if (!matchedUser) continue;
                const restKey = code.slice(matchedUser.length + 1);
                if (!categorized[matchedUser]) categorized[matchedUser] = {};
                categorized[matchedUser][restKey] = { recognizedText: recognized, ocrStatus: "done" } as IOcrResult;
            }
            const answerItems = this.items.map(it => ({
                userCode: it.userCode,
                range: it.range,
                codeToRecognizedText: [categorized[it.userCode] ?? {}],
                submissionCode: it.submissionCode
            }));
            if (this.answerOffset == null) this.answerOffset = this.registerSegment(answerItems.length);
            const baseAnswerCallbacks = this.createDelegatingCallbacks({ offset: this.gaiOffset, count: answerItems.length });
            const answerCallbacks: UploadCallbacks = {
                ...baseAnswerCallbacks,
                onItemError: (i, item, e) => {
                    baseAnswerCallbacks.onItemError?.(i, item, e);
                },
                onComplete: ({ failedIndices }) => {
                    baseAnswerCallbacks.onComplete?.({ failedIndices });
                }
            };
            this.answerUploader = new AnswerBatchUploader(this.projectCode, this.examInfoArray, this.projectSubmissionStore, this.projectStore, answerItems);
            this.answerUploader.setCallbacks(answerCallbacks);
        }
        const answerResumeResult = await this.answerUploader.resume();
        if (!answerResumeResult.allSucceeded) {
            this.notifyPipelineComplete('failure', 'answer', answerResumeResult.failedIndices ?? []);
            return; // stop pipeline if Answer had failures
        }
        this.notifyPipelineComplete('success', 'answer', []);
    }

	public getGAIResult(): Record<string, string> | null {
		return this.gaiResult;
	}

    // ===== Delegating callback handler for managing multiple uploaders =====
    private emitStart(total: number) {
        this.callbacks?.onStart?.(total);
    }

    private emitItemStart(index: number, item: USBBatchUploadItem) {
        this.callbacks?.onItemStart?.(index, item);
    }

    private emitItemSuccess(index: number, item: USBBatchUploadItem) {
        this.callbacks?.onItemSuccess?.(index, item);
    }

    private emitItemError(index: number, item: USBBatchUploadItem, error: unknown) {
        this.callbacks?.onItemError?.(index, item, error);
    }

    private emitProgress(nextIndex: number, total: number, failed: number[]) {
        this.callbacks?.onProgress?.(nextIndex, total, failed);
    }

    private emitComplete(result: { failedIndices: number[] }) {
        this.callbacks?.onComplete?.(result);
    }

    private getTotalCount(): number {
        if (!this.segments || this.segments.length === 0) return this.items?.length ?? 0;
        return this.segments.reduce((sum, s) => sum + (s?.count ?? 0), 0);
    }

    /**
     * Registers a logical segment for an uploader and returns its global offset.
     * Use this when orchestrating multiple uploader instances under one processor.
     */
    public registerSegment(count: number): number {
        const currentTotal = this.getTotalCount();
        const offset = currentTotal;
        this.segments.push({ offset, count });
        return offset;
    }

    /**
     * Emits a single, high-level completion-style callback for the entire pipeline
     * indicating success, failure, stopped/waiting, and which stage it pertains to.
     * This uses the same callback channel as item uploaders (onComplete) but with
     * aggregated failed indices when applicable.
     */
    public notifyPipelineComplete(
        status: 'success' | 'failure' | 'stopped' | 'waiting',
        stage: 'usb' | 'gai' | 'answer',
        failedIndices: number[]
    ): void {
        // Optionally, external listeners can differentiate by inspecting logs
        // or by correlating the last stage-specific completion.
        try {
//            this.callbacks?.onComplete?.({ failedIndices: failedIndices ?? [] });
            this.callbacks?.onCompleteHighLevel?.(status);
        } catch (_e) {
            // ignore
        }
    }

    /**
     * Creates a set of callbacks that map a child uploader's local indices to
     * the processor's global index space, and delegates events to external listeners.
     */
    public createDelegatingCallbacks(params: { offset: number; count: number }): UploadCallbacks {
        const { offset, count } = params;
        // Ensure segment is known for accurate totals
        if (!this.segments.find(s => s.offset === offset && s.count === count)) {
            this.segments.push({ offset, count });
        }
        const total = () => this.getTotalCount();
        return {
            onStart: () => this.emitStart(total()),
            onItemStart: (i, item) => this.emitItemStart(offset + i, item),
            onItemSuccess: (i, item) => this.emitItemSuccess(offset + i, item),
            onItemError: (i, item, error) => this.emitItemError(offset + i, item, error),
            onProgress: (nextIndex, _t, failed) => {
                const mappedFailed = (failed || []).map(idx => offset + idx);
                this.emitProgress(offset + nextIndex, total(), mappedFailed);
            },
            onComplete: ({ failedIndices }) => {
                const mappedFailed = (failedIndices || []).map(idx => offset + idx);
                this.emitComplete({ failedIndices: mappedFailed });
            }
        };
    }

}